/**
 * Party picker slide-over.
 *
 * Two use cases (both call the same component):
 *   1. **Inline picker** on the Create Transaction form — caller
 *      sets `defaultName=""` and uses `onPick(party)` to learn the
 *      selected `{ id, name, partyType }`.
 *   2. **Link-from-name** on the Party tab — caller passes the
 *      unlinked name as `defaultName`, the search input pre-fills,
 *      and the user can either pick an existing party (which the
 *      caller backfills onto the matching transactions) or hit the
 *      "+ Create new party '{name}'" CTA which opens the full
 *      `PartyFormSlideOver` with the name pre-filled.
 *
 * After a successful pick OR after the user creates a fresh party,
 * `onPick(party)` fires and the caller is responsible for closing
 * the slide-over.
 *
 * Reads from `useParties()` (org-wide live snapshot). Filters
 * client-side by typed query. Sorted by name.
 */
import { useEffect, useMemo, useState } from 'react';

import { PartyFormSlideOver } from '@/components/PartyFormSlideOver';
import {
  formatIndianPhone,
  partyTypeColors,
  partyTypeLabel,
  type Party,
} from '@/lib/parties';
import { useParties } from '@/lib/useParties';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

export function PartyPickerSlideOver({
  open,
  onClose,
  orgId,
  defaultName = '',
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  /** Pre-filled search query (and pre-filled name for the
   *  "+ Create new" CTA + the underlying create form). Pass the
   *  typed/unlinked name when linking from the Party tab. */
  defaultName?: string;
  /** Called when the user picks an existing party OR creates a new
   *  one. Caller closes the picker. */
  onPick: (party: Pick<Party, 'id' | 'name' | 'partyType'>) => void;
}) {
  const { parties, loading } = useParties();
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  // Reset query to default whenever the picker opens.
  useEffect(() => {
    if (open) setQuery(defaultName);
    if (!open) setCreateOpen(false);
  }, [open, defaultName]);

  // Body-scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, createOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return parties;
    return parties.filter((p) => {
      const blob = [
        p.name,
        p.phone,
        p.email ?? '',
        partyTypeLabel(p.partyType),
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [parties, query]);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-address-book" /> Pick a party
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <i
              className="ph ph-magnifying-glass"
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)',
                fontSize: 14,
              }}
            />
            <input
              type="text"
              placeholder="Search parties by name, phone, or type…"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px 10px 36px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                background: '#FAFAFA',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Create-new CTA — always visible, pre-fills the typed
              query into the full create form. */}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={{
              padding: 12,
              border: '1.5px dashed var(--brand)',
              background: 'var(--brand-bg)',
              color: 'var(--brand)',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <i className="ph-bold ph-plus" />
            {query.trim()
              ? `Create new party "${query.trim()}"`
              : 'Create new party'}
          </button>

          {/* List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {loading ? (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                Loading parties…
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                {query.trim()
                  ? `No parties match "${query.trim()}". Use the button above to create one.`
                  : 'No parties yet — create your first.'}
              </div>
            ) : (
              filtered.map((p) => {
                const c = partyTypeColors(p.partyType);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() =>
                      onPick({
                        id: p.id,
                        name: p.name,
                        partyType: p.partyType,
                      })
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 10,
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      background: '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: c.bg,
                        color: c.fg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials(p.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                          display: 'flex',
                          gap: 6,
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: c.bg,
                            color: c.fg,
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                          }}
                        >
                          {partyTypeLabel(p.partyType)}
                        </span>
                        <span>{formatIndianPhone(p.phone)}</span>
                      </div>
                    </div>
                    <i
                      className="ph-bold ph-caret-right"
                      style={{ color: 'var(--text-tertiary)' }}
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Nested full create form. When the user saves, `onSaved` fires
          with the new partyId; we pass it up to the picker's caller
          and close the picker. */}
      <PartyFormSlideOver
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        orgId={orgId}
        party={null}
        defaultName={query.trim() || undefined}
        onSaved={(id) => {
          setCreateOpen(false);
          // We don't have the full Party doc back from createParty;
          // fish it out of the live list we already subscribe to.
          const created = parties.find((p) => p.id === id);
          if (created) {
            onPick({
              id: created.id,
              name: created.name,
              partyType: created.partyType,
            });
          } else {
            // Snapshot hasn't caught up yet — use the typed name as a
            // placeholder. Picker caller updates on next snapshot.
            onPick({
              id,
              name: query.trim() || 'Party',
              partyType: 'other_vendor',
            });
          }
        }}
      />
    </>
  );
}
